/**
 * PopoverFilter
 *
 * Reusable animated popover filter component for multi-select filtering.
 * Used in the OrdersFilterBar for product type, color, size, tags, and queue status filters.
 *
 * Features:
 * - Animated popover with framer-motion enter/exit transitions
 * - Multi-select checkboxes with sanitized values
 * - Badge count showing active selections
 * - Proper ARIA labels for accessibility
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ANIMATIONS, getAnimationProps } from '@/lib/animations';

interface PopoverFilterProps {
  label: string;
  icon: LucideIcon;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  /** Optional display labels for options (e.g., 'ready' -> 'Ready to Batch') */
  optionLabels?: Record<string, string>;
}

/**
 * Sanitization helper to prevent XSS and limit length.
 * Pattern from existing FilterPanel.tsx
 */
export function sanitizeFilterValue(value: string): string {
  return value.trim().slice(0, 50);
}

export function PopoverFilter({
  label,
  icon: Icon,
  options,
  selected,
  onChange,
  disabled = false,
  optionLabels
}: PopoverFilterProps) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(false);
  const shouldReduceMotion = useReducedMotion();
  const animProps = getAnimationProps(ANIMATIONS.popover, shouldReduceMotion);

  const handleToggle = (option: string, checked: boolean) => {
    const sanitized = sanitizeFilterValue(option);
    if (checked) {
      onChange([...selected, sanitized]);
    } else {
      onChange(selected.filter(s => s !== sanitized));
    }
  };

  const getOptionLabel = (option: string): string => {
    if (optionLabels && optionLabels[option]) {
      return optionLabels[option];
    }
    return sanitizeFilterValue(option);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled || options.length === 0}
          aria-label={`Filter by ${label}${selected.length > 0 ? `, ${selected.length} selected` : ''}`}
          aria-expanded={open}
          aria-haspopup="listbox"
          className="gap-2"
        >
          <Icon className="w-4 h-4" />
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-1 rounded-full px-2 text-xs">
              {selected.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <AnimatePresence>
        {open && (
          <PopoverContent
            asChild
            align="start"
            className="w-64 p-0"
            forceMount
          >
            <motion.div
              {...animProps}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <div className="p-3 border-b">
                <p className="font-medium text-sm">{t('orders.filter.filterBy', { label })}</p>
              </div>
              <div
                role="listbox"
                aria-label={`${label} filter options`}
                className="max-h-60 overflow-y-auto p-2 space-y-1"
              >
                {options.map(option => {
                  const sanitized = sanitizeFilterValue(option);
                  const isChecked = selected.includes(sanitized);
                  const displayLabel = getOptionLabel(option);
                  return (
                    <label
                      key={option}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => handleToggle(option, !!checked)}
                        aria-label={`${displayLabel}${isChecked ? ', selected' : ''}`}
                      />
                      <span className="text-sm truncate">{displayLabel}</span>
                    </label>
                  );
                })}
                {options.length === 0 && (
                  <p className="text-sm text-muted-foreground px-2 py-4 text-center">
                    {t('orders.filter.noOptionsAvailable')}
                  </p>
                )}
              </div>
            </motion.div>
          </PopoverContent>
        )}
      </AnimatePresence>
    </Popover>
  );
}
